import { Link } from "react-router-dom";

interface FailCardProps {
  id: string;
  title: string;
  category: string;
  categorySlug: string;
  excerpt: string;
  cover: string;
  date: string;
  views: string;
  readTime: string;
  failIndex: number;
  variant?: "default" | "wide" | "minimal";
}

function FailMeter({ value }: { value: number }) {
  const segments = Array.from({ length: 10 }, (_, index) => index < value);
  return (
    <span className="flex items-center gap-0.5" aria-label={`翻车指数 ${value} / 10`}>
      {segments.map((on, index) => (
        <span
          key={index}
          className={`w-1 h-3 rounded-sm ${on ? "bg-primary-500" : "bg-background-300"}`}
        ></span>
      ))}
      <span className="ml-1 text-xs font-semibold text-primary-600">{value}</span>
    </span>
  );
}

export default function FailCard({
  id,
  title,
  category,
  categorySlug,
  excerpt,
  cover,
  date,
  views,
  readTime,
  failIndex,
  variant = "default",
}: FailCardProps) {
  if (variant === "minimal") {
    return (
      <article className="group flex gap-3 py-4 border-b border-background-200 last:border-b-0">
        <Link
          to={`/article/${id}`}
          className="shrink-0 w-24 h-20 rounded-md overflow-hidden bg-background-200"
        >
          <img src={cover} alt={title} className="w-full h-full object-cover object-top" />
        </Link>
        <div className="flex flex-col justify-between min-w-0">
          <h3 className="text-sm font-medium text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            <Link to={`/article/${id}`}>{title}</Link>
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-2 flex-wrap">
            <span className="text-primary-600">{category}</span>
            <span className="whitespace-nowrap">{date}</span>
            <span className="whitespace-nowrap">
              <i className="ri-eye-line mr-0.5"></i>
              {views}
            </span>
          </div>
        </div>
      </article>
    );
  }

  const isWide = variant === "wide";

  return (
    <article
      className={`group h-full bg-background-50 border border-background-200 rounded-lg overflow-hidden flex ${
        isWide ? "flex-col md:flex-row" : "flex-col"
      }`}
    >
      <Link
        to={`/article/${id}`}
        className={`block overflow-hidden bg-background-200 ${isWide ? "md:w-[46%] w-full h-56 md:h-auto" : "w-full h-48"}`}
      >
        <img
          src={cover}
          alt={title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
      </Link>
      <div className={`flex flex-col flex-1 ${isWide ? "p-5 md:p-7" : "p-4 md:p-5"}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Link
            to={`/category?cat=${categorySlug}`}
            className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap hover:bg-secondary-200 transition-colors"
          >
            {category}
          </Link>
          {isWide && (
            <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-flashlight-line"></i>
              翻车指数
            </span>
          )}
          {isWide && <FailMeter value={failIndex} />}
        </div>
        <h3
          className={`font-heading font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors ${
            isWide ? "text-xl md:text-2xl mb-3" : "text-base mb-2"
          }`}
        >
          <Link to={`/article/${id}`}>{title}</Link>
        </h3>
        <p className={`text-foreground-600 leading-relaxed ${isWide ? "text-sm md:text-base mb-5" : "text-xs mb-4 line-clamp-2"}`}>
          {excerpt}
        </p>
        <div className="mt-auto flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
          <span className="whitespace-nowrap">{date}</span>
          <span className="w-px h-3 bg-background-300"></span>
          <span className="whitespace-nowrap">
            <i className="ri-eye-line mr-0.5"></i>
            {views}
          </span>
          <span className="w-px h-3 bg-background-300"></span>
          <span className="whitespace-nowrap">
            <i className="ri-time-line mr-0.5"></i>
            {readTime}
          </span>
        </div>
      </div>
    </article>
  );
}